/**
 * PdwStatusBadges — PDW Step 9 status-badge row (SPEC Screen 17, line 1545).
 *
 * 4 status badges showing cart-state progress for the current person:
 *   - CITIZENSHIP — ✓ when citizenship status is set
 *   - LAWFUL PRESENCE — ✓ when US Citizen / LPR (implicit) or NRA visa + I-94 captured
 *   - IDENTITY VERIFIED — ✓ when Photo + Name + DOB all captured (3-of-3)
 *   - TAX ID — ✓ when SSN/ITIN/EIN captured per F92 type, OR F92 = N
 *     (None required) which is a valid set state (Bill ruling #4, 2026-06-12)
 *
 * Each badge: label (10px UPPERCASE), value (13px), status icon (✓ / ? / ⚠).
 * Reactive — re-derives from person on every render.
 *
 * Bill 2026-05-13 — PDW Step 9 first sub-batch (badges-only); the 9-tab
 * strip + section segmentation + Verify-tab OFAC ship in subsequent
 * v0.14.38 / v0.14.39 / v0.14.40 batches.
 */
import type { RaoPerson } from '../../types';

export interface PdwStatusBadgesProps {
  person: RaoPerson;
}

type BadgeStatus = 'done' | 'pending' | 'warn';

interface BadgeSpec {
  readonly key: string;
  readonly label: string;
  readonly status: BadgeStatus;
  readonly value: string;
}

function deriveBadges(person: RaoPerson): ReadonlyArray<BadgeSpec> {
  const cs = person.citizenshipStatus;
  const isUsCitizen = cs === 'US Citizen';
  const isLpr = cs === 'Permanent Resident';
  const isNra = cs === 'Non-Resident Alien';

  // CITIZENSHIP
  const citizenshipBadge: BadgeSpec = {
    key: 'citizenship',
    label: 'Citizenship',
    status: cs ? 'done' : 'pending',
    value: cs || '— not set —',
  };

  // LAWFUL PRESENCE
  const hasVisaAndI94 = !!person.visaType && !!person.i94Number;
  const lawfulPresenceDone = isUsCitizen || isLpr || (isNra && hasVisaAndI94);
  const lawfulPresenceBadge: BadgeSpec = {
    key: 'lawful-presence',
    label: 'Lawful presence',
    status: lawfulPresenceDone ? 'done' : (cs ? 'warn' : 'pending'),
    value: isUsCitizen
      ? 'US — implicit'
      : isLpr
        ? 'LPR — I-551'
        : isNra && hasVisaAndI94
          ? `${person.visaType} + I-94`
          : isNra
            ? 'Need visa + I-94'
            : '— pending —',
  };

  // IDENTITY VERIFIED — Photo + Name + DOB
  const hasName = !!(person.firstName && person.lastName);
  const hasDob = !!person.dob;
  // "Photo captured" proxy — any freshly-attached doc this session.
  const extra = (person.extra as Record<string, unknown> | undefined) ?? {};
  const attachedDocs = extra['attachedDocs'];
  const hasFreshPhotoId = Array.isArray(attachedDocs) && attachedDocs.length > 0;
  // F1 (Bill 2026-06-03): an existing CIF carries its CIP identity ON FILE in
  // Core Director — CustInq hydration seeds extra.idType/idNumber/idExpiryDate
  // (useInquiryHydration L223-227). That on-file CIP satisfies the photo-ID
  // axis; we do NOT re-require a fresh capture for an existing customer
  // (procedure: trust the prior-pass baseline; CIP-EXPIRATION is the only
  // re-capture trigger, enforced on the Verify tab). Without this the badge
  // sat "2 of 3" forever on an existing CIF that was fully verified on file.
  // F2 (Bill 2026-06-03): a CIP primary ID being ESTABLISHED (extra.idType
  // present — captured, manually typed, or CustInq-hydrated) satisfies the
  // identity axis: the ID is on record. This keeps "Identity verified"
  // consistent with the Verify-tab idScan section (both key off the CIP ID),
  // so the rollup banner can no longer say "Verify: Cleared" while this badge
  // reads "2 of 3". Generalizes the F1 existing-CIF case to any customer.
  const hasCipId =
    typeof extra['idType'] === 'string' && (extra['idType'] as string).trim() !== '';
  const onFileCip = !!(person.cifId ?? '').trim() && hasCipId;
  const hasPhotoId = hasFreshPhotoId || hasCipId;
  const idCount = [hasName, hasDob, hasPhotoId].filter(Boolean).length;
  const photoLabel = hasFreshPhotoId ? 'photo' : onFileCip ? 'on-file' : hasCipId ? 'ID' : '—';
  const identityBadge: BadgeSpec = {
    key: 'identity-verified',
    label: 'Identity verified',
    status: idCount === 3 ? 'done' : idCount > 0 ? 'pending' : 'pending',
    value: `${idCount} of 3 (${hasName ? 'name' : '—'} · ${hasDob ? 'DOB' : '—'} · ${photoLabel})`,
  };

  // TAX ID — Field 92 (RaoPerson.tinCode) + RaoPerson.taxId.
  //   N (None required)  → VALID set state, ok/neutral tone (NOT "— not set —").
  //                        No-TIN / Field-92 control, Bill ruling #4 (2026-06-12).
  //   S/E/Forn + 9 digits → set (done).
  //   S/E/Forn, no number → pending.
  const taxId = person.taxId;
  const tinCode = person.tinCode;
  const hasTaxId = !!taxId && taxId.length >= 9;
  const taxIdBadge: BadgeSpec =
    tinCode === 'N'
      ? {
          key: 'tax-id',
          label: 'Tax ID',
          // 'done' carries the ok/neutral (green ✓) tone — N is a complete,
          // valid Field-92 disposition, not an unfinished field.
          status: 'done',
          value: 'N · None required',
        }
      : {
          key: 'tax-id',
          label: 'Tax ID',
          status: hasTaxId ? 'done' : 'pending',
          value: hasTaxId
            ? `${tinCode ?? 'S'} · ***-**-${taxId!.slice(-4)}`
            : '— pending —',
        };

  return [citizenshipBadge, lawfulPresenceBadge, identityBadge, taxIdBadge];
}

const STATUS_ICON: Record<BadgeStatus, string> = {
  'done':    '✓',
  'pending': '?',
  'warn':    '⚠',
};

export function PdwStatusBadges({ person }: PdwStatusBadgesProps): JSX.Element {
  const badges = deriveBadges(person);
  return (
    <div className="pdw-status-badges" role="list" aria-label="Person status">
      {badges.map((b) => (
        <div
          key={b.key}
          role="listitem"
          className={`pdw-status-badge pdw-status-badge--${b.status}`}
        >
          <div className="pdw-status-badge__icon" aria-hidden>{STATUS_ICON[b.status]}</div>
          <div className="pdw-status-badge__body">
            <div className="pdw-status-badge__label">{b.label}</div>
            <div className="pdw-status-badge__value">{b.value}</div>
          </div>
        </div>
      ))}
    </div>
  );
}
